// ═══════════════════════════════════════════════════════════════
// ChatPanel.jsx — User-side live support chat
// Real-time messaging, typing indicators, read receipts,
// agent availability detection with offline fallback to tickets
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { PATHS, CHAT_STATUS, generateId } from './HelpCenterConfig.js';
import { resolveRecordScope, ownerQuery, listRecords } from './recordScope.js';

/**
 * Props:
 *   storage          — StorageAdapter instance
 *   pushAdapter      — PushAdapter instance
 *   presenceManager  — PresenceManager instance
 *   user             — { id, name, email, role }
 *   context          — optional { conflictId, page, etc. } for contextual support
 *   config           — merged HelpCenterConfig
 *   onCreateTicket   — callback(initialMessage) when no agents available; parent switches to ticket flow
 */
function ChatPanel({ storage, pushAdapter, presenceManager, user, context, config, onCreateTicket }) {
  const [chatId, setChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [chatStatus, setChatStatus] = useState(null);       // null = no chat, waiting, active, ended
  const [agentInfo, setAgentInfo] = useState(null);          // { id, name } of assigned agent
  const [agentsOnline, setAgentsOnline] = useState([]);
  const [agentTyping, setAgentTyping] = useState(false);
  const [pastChats, setPastChats] = useState([]);
  const [viewingPastChat, setViewingPastChat] = useState(null);
  const [initializing, setInitializing] = useState(true);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const typingTimerRef = useRef(null);
  const unsubMessagesRef = useRef(null);
  const unsubChatRef = useRef(null);
  const unsubTypingRef = useRef(null);
  const unsubPresenceRef = useRef(null);

  const maxLen = config?.maxMessageLength || 2000;
  const msgs = config?.messages || {};

  // ── Subscribe to agent presence ──
  useEffect(() => {
    if (!presenceManager) { setInitializing(false); return; }
    unsubPresenceRef.current = presenceManager.subscribeToPresence((agents) => {
      setAgentsOnline(agents);
    });
    // Also do an initial check
    presenceManager.getOnlineAgents().then(agents => {
      setAgentsOnline(agents);
      setInitializing(false);
    }).catch(() => setInitializing(false));

    return () => {
      if (unsubPresenceRef.current) unsubPresenceRef.current();
    };
  }, [presenceManager]);

  // ── Load past chats for this user ──
  useEffect(() => {
    if (!storage || !user?.id) return;
    let cancelled = false;
    const loadPast = async () => {
      try {
        const scope = await resolveRecordScope(storage, user);
        const allChats = await listRecords(storage, PATHS.chatSessions(), ownerQuery(scope));
        if (cancelled) return;
        const mine = (allChats || [])
          .filter(c => c.userId === user.id)
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setPastChats(mine);

        // Resume active/waiting chat if exists
        const active = mine.find(c => c.status === CHAT_STATUS.ACTIVE || c.status === CHAT_STATUS.WAITING);
        if (active) {
          setChatId(active._key || active.id);
          setChatStatus(active.status);
          if (active.agentId) setAgentInfo({ id: active.agentId, name: active.agentName || 'Support' });
        }
      } catch (e) { /* best effort */ }
    };
    loadPast();
    return () => { cancelled = true; };
  }, [storage, user?.id]);

  // ── Subscribe to current chat metadata ──
  useEffect(() => {
    if (!storage || !chatId) return;
    unsubChatRef.current = storage.subscribe(PATHS.chatSession(chatId), (data) => {
      if (!data) return;
      setChatStatus(data.status);
      if (data.agentId && !agentInfo) {
        setAgentInfo({ id: data.agentId, name: data.agentName || 'Support' });
      }
    });
    return () => { if (unsubChatRef.current) unsubChatRef.current(); };
  }, [storage, chatId]);

  // ── Subscribe to messages for current chat ──
  useEffect(() => {
    if (!storage || !chatId) return;
    unsubMessagesRef.current = storage.subscribe(PATHS.chatMessages(chatId), (data) => {
      if (!data) { setMessages([]); return; }
      const list = Object.entries(data)
        .map(([key, val]) => ({ ...val, _key: key }))
        .sort((a, b) => new Date(a.sentAt) - new Date(b.sentAt));
      setMessages(list);

      // Mark unread agent messages as read
      if (user?.id) {
        list.forEach(msg => {
          if (msg.senderId !== user.id && !msg.readBy?.[user.id]) {
            storage.update(`${PATHS.chatMessages(chatId)}/${msg._key}/readBy`, {
              [user.id]: new Date().toISOString(),
            }).catch(() => {});
          }
        });
      }
    });
    return () => { if (unsubMessagesRef.current) unsubMessagesRef.current(); };
  }, [storage, chatId, user?.id]);

  // ── Subscribe to typing indicator ──
  useEffect(() => {
    if (!storage || !chatId || !config?.enableTypingIndicators) return;
    unsubTypingRef.current = storage.subscribe(PATHS.chatTyping(chatId), (data) => {
      if (!data) { setAgentTyping(false); return; }
      // Check if any non-user participant is typing within last 5s
      const now = Date.now();
      const someoneTyping = Object.entries(data).some(([id, record]) => {
        if (id === user?.id) return false;
        const ts = new Date(record.timestamp).getTime();
        return now - ts < 5000;
      });
      setAgentTyping(someoneTyping);
    });
    return () => { if (unsubTypingRef.current) unsubTypingRef.current(); };
  }, [storage, chatId, config?.enableTypingIndicators, user?.id]);

  // ── Auto-scroll on new messages ──
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, agentTyping]);

  // ── Start a new chat ──
  const startChat = useCallback(async (firstMessage) => {
    if (!storage || !user?.id || !firstMessage.trim()) return;
    if (firstMessage.trim().length > maxLen) return;

    // Check agent availability
    const hasAgent = agentsOnline.some(a => a.status === 'online');

    if (!hasAgent && onCreateTicket) {
      // No agents online — route to ticket
      onCreateTicket(firstMessage.trim());
      return;
    }

    const newChatId = generateId('chat');
    // Organization stamp lets same-org admins see the chat (rules check it)
    const { tenantId } = await resolveRecordScope(storage, user);
    const chatRecord = {
      id: newChatId,
      userId: user.id,
      tenantId,
      userName: user.name || 'User',
      userEmail: user.email || null,
      context: context || null,
      status: CHAT_STATUS.WAITING,
      agentId: null,
      agentName: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    try {
      await storage.set(PATHS.chatSession(newChatId), chatRecord);

      // Send first message
      const msgRecord = {
        id: generateId('msg'),
        senderId: user.id,
        senderName: user.name || 'User',
        senderRole: 'user',
        text: firstMessage.trim(),
        sentAt: new Date().toISOString(),
        readBy: { [user.id]: new Date().toISOString() },
      };
      await storage.push(PATHS.chatMessages(newChatId), msgRecord);

      setChatId(newChatId);
      setChatStatus(CHAT_STATUS.WAITING);
      setInput('');

      // Notify agents
      if (pushAdapter) {
        pushAdapter.sendToRole('support', {
          title: 'New Support Chat',
          body: 'A new support chat request has been submitted.',
          type: 'help-center-chat',
          data: { chatId: newChatId, userId: user.id },
        }).catch(() => {});
      }
    } catch (e) {
      console.error('[ChatPanel] Failed to start chat:', e);
    }
  }, [storage, pushAdapter, user, context, agentsOnline, onCreateTicket]);

  // ── Send a message in active chat ──
  const sendMessage = useCallback(async () => {
    if (!storage || !chatId || !input.trim() || sending) return;
    if (input.trim().length > maxLen) return;
    setSending(true);

    const msgRecord = {
      id: generateId('msg'),
      senderId: user.id,
      senderName: user.name || 'User',
      senderRole: 'user',
      text: input.trim(),
      sentAt: new Date().toISOString(),
      readBy: { [user.id]: new Date().toISOString() },
    };

    try {
      await storage.push(PATHS.chatMessages(chatId), msgRecord);
      await storage.update(PATHS.chatSession(chatId), { updatedAt: new Date().toISOString() });
      setInput('');

      // Clear typing indicator
      if (config?.enableTypingIndicators) {
        storage.remove(`${PATHS.chatTyping(chatId)}/${user.id}`).catch(() => {});
      }

      // Notify agent if assigned
      if (agentInfo?.id && pushAdapter) {
        pushAdapter.sendToUser(agentInfo.id, {
          title: 'New Message',
          body: 'You have a new message in a support chat.',
          type: 'help-center-chat-message',
          data: { chatId },
        }).catch(() => {});
      }

      if (inputRef.current) inputRef.current.focus();
    } catch (e) {
      console.error('[ChatPanel] Failed to send message:', e);
    }
    setSending(false);
  }, [storage, pushAdapter, chatId, input, sending, user, agentInfo, config]);

  // ── Typing indicator ──
  const handleInputChange = (e) => {
    const val = e.target.value;
    if (val.length <= maxLen) setInput(val);

    if (config?.enableTypingIndicators && chatId && storage) {
      storage.set(`${PATHS.chatTyping(chatId)}/${user.id}`, {
        timestamp: new Date().toISOString(),
      }).catch(() => {});

      // Clear typing after 3s of no input
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
      typingTimerRef.current = setTimeout(() => {
        storage.remove(`${PATHS.chatTyping(chatId)}/${user.id}`).catch(() => {});
      }, 3000);
    }
  };

  // ── End chat ──
  const endChat = async () => {
    if (!storage || !chatId) return;
    try {
      await storage.update(PATHS.chatSession(chatId), {
        status: CHAT_STATUS.ENDED,
        endedAt: new Date().toISOString(),
        endedBy: user.id,
      });
      setChatStatus(CHAT_STATUS.ENDED);

      // Clean up typing
      if (config?.enableTypingIndicators) {
        storage.remove(`${PATHS.chatTyping(chatId)}/${user.id}`).catch(() => {});
      }
    } catch (e) {
      console.error('[ChatPanel] Failed to end chat:', e);
    }
  };

  // ── Start a new chat after previous one ended ──
  const startNewChat = () => {
    setChatId(null);
    setChatStatus(null);
    setMessages([]);
    setAgentInfo(null);
    setAgentTyping(false);
    setViewingPastChat(null);
  };

  // ── View a past chat ──
  const viewPastChat = async (chat) => {
    if (!storage) return;
    try {
      const data = await storage.get(PATHS.chatMessages(chat._key || chat.id));
      const list = data
        ? Object.entries(data)
            .map(([key, val]) => ({ ...val, _key: key }))
            .sort((a, b) => new Date(a.sentAt) - new Date(b.sentAt))
        : [];
      setViewingPastChat({ ...chat, messages: list });
    } catch (e) { /* best effort */ }
  };

  // ── Key handler ──
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!chatId) {
        startChat(input);
      } else {
        sendMessage();
      }
    }
  };

  // ── Icons ──
  const SendIcon = () => (
    <svg aria-hidden="true" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path d="m22 2-7 20-4-9-9-4 20-7z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  const ClockIcon = () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" strokeLinecap="round" />
    </svg>
  );
  const ChatIcon = () => (
    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  const XIcon = () => (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path d="M6 18 18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  const ArrowLeftIcon = () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path d="m15 18-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  const CheckIcon = ({ double }) => (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
      {double && <path d="M11 12l5 5L26 7" strokeLinecap="round" strokeLinejoin="round" />}
    </svg>
  );

  const primaryColor = config?.primaryColor || '#d97706';

  // ── Loading ──
  if (initializing) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-4 border-stone-200 rounded-full animate-spin" style={{ borderTopColor: primaryColor }} />
      </div>
    );
  }

  // ── Viewing a past chat ──
  if (viewingPastChat) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center gap-3 p-3 border-b border-stone-200">
          <button onClick={() => setViewingPastChat(null)} className="p-1 hover:bg-stone-100 rounded-lg">
            <ArrowLeftIcon />
          </button>
          <div>
            <p className="text-sm font-medium text-stone-800">
              Chat with {viewingPastChat.agentName || 'Support'}
            </p>
            <p className="text-xs text-stone-500">
              {new Date(viewingPastChat.createdAt).toLocaleDateString()} · {viewingPastChat.status}
            </p>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {viewingPastChat.messages.map((msg) => (
            <MessageBubble key={msg._key || msg.id} msg={msg} isOwn={msg.senderId === user.id} primaryColor={primaryColor} />
          ))}
        </div>
      </div>
    );
  }

  // ── No active chat — show start screen ──
  if (!chatId || chatStatus === CHAT_STATUS.ENDED) {
    const hasOnlineAgent = agentsOnline.some(a => a.status === 'online');

    return (
      <div className="space-y-6">
        {/* Status indicator */}
        <div className="text-center pt-4">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: `${primaryColor}20` }}>
            <ChatIcon />
          </div>
          <h3 className="text-lg font-semibold text-stone-800">Live Support</h3>
          <div className="flex items-center justify-center gap-2 mt-2">
            <span className={`w-2.5 h-2.5 rounded-full ${hasOnlineAgent ? 'bg-green-500' : 'bg-stone-300'}`} />
            <span className="text-sm text-stone-500">
              {hasOnlineAgent
                ? `${agentsOnline.filter(a => a.status === 'online').length} agent(s) online`
                : 'Support is offline'}
            </span>
          </div>
        </div>

        {/* Chat ended message */}
        {chatStatus === CHAT_STATUS.ENDED && (
          <div className="p-3 bg-stone-50 rounded-lg text-center">
            <p className="text-sm text-stone-500">{msgs.chatEnded || 'This chat has ended. Thank you!'}</p>
          </div>
        )}

        {/* Compose first message */}
        <div className="px-2">
          <label className="block text-sm font-medium text-stone-700 mb-2">
            {hasOnlineAgent ? 'How can we help you?' : "Leave us a message and we'll get back to you"}
          </label>
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => e.target.value.length <= maxLen && setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (input.trim()) startChat(input);
              }
            }}
            placeholder="Type your message..."
            rows={3}
            className="w-full p-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent resize-none text-stone-700 placeholder-stone-500"
            style={{ '--tw-ring-color': primaryColor }}
          />
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-stone-500">{input.length}/{maxLen}</span>
            <button
              onClick={() => input.trim() && startChat(input)}
              disabled={!input.trim()}
              className="px-5 py-2 text-white rounded-lg font-medium transition-colors disabled:opacity-50 hover:opacity-90"
              style={{ backgroundColor: primaryColor }}
            >
              {hasOnlineAgent ? 'Start Chat' : 'Send Message'}
            </button>
          </div>
        </div>

        {/* Past chats */}
        {pastChats.length > 0 && (
          <div className="px-2">
            <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Previous Conversations</p>
            <div className="space-y-1.5">
              {pastChats.slice(0, 5).map((chat) => (
                <button
                  key={chat._key || chat.id}
                  onClick={() => viewPastChat(chat)}
                  className="w-full flex items-center justify-between p-3 bg-stone-50 hover:bg-stone-100 rounded-lg transition-colors text-left"
                >
                  <div className="min-w-0">
                    <p className="text-sm text-stone-700 truncate">
                      {chat.agentName ? `Chat with ${chat.agentName}` : 'Support Chat'}
                    </p>
                    <p className="text-xs text-stone-500">
                      {new Date(chat.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                    chat.status === CHAT_STATUS.ENDED
                      ? 'bg-stone-200 text-stone-600'
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {chat.status === CHAT_STATUS.ENDED ? 'Ended' : 'Active'}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Active / Waiting chat ──
  return (
    <div className="flex flex-col h-full" style={{ minHeight: '400px' }}>
      {/* Chat Header */}
      <div className="flex items-center justify-between p-3 border-b border-stone-200 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0" style={{ backgroundColor: primaryColor }}>
            {agentInfo?.name?.[0]?.toUpperCase() || 'S'}
          </div>
          <div>
            <p className="text-sm font-medium text-stone-800">
              {agentInfo?.name || 'Support'}
            </p>
            <p className="text-xs text-stone-500">
              {chatStatus === CHAT_STATUS.WAITING
                ? msgs.chatConnecting || 'Connecting you with support...'
                : agentTyping ? 'Typing...' : 'Online'}
            </p>
          </div>
        </div>
        {chatStatus !== CHAT_STATUS.ENDED && (
          <button
            onClick={endChat}
            className="text-xs text-stone-500 hover:text-red-600 px-2 py-1 rounded hover:bg-red-50 transition-colors"
          >
            End Chat
          </button>
        )}
      </div>

      {/* Waiting indicator */}
      {chatStatus === CHAT_STATUS.WAITING && (
        <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border-b border-amber-200">
          <ClockIcon />
          <p className="text-xs text-amber-700">
            Waiting for an agent to join. We'll notify you when someone connects.
          </p>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((msg) => (
          <MessageBubble
            key={msg._key || msg.id}
            msg={msg}
            isOwn={msg.senderId === user.id}
            primaryColor={primaryColor}
            showReadReceipt={config?.enableReadReceipts}
          />
        ))}

        {/* Typing indicator */}
        {agentTyping && (
          <div className="flex items-start gap-2">
            <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: primaryColor }}>
              {agentInfo?.name?.[0]?.toUpperCase() || 'S'}
            </div>
            <div className="bg-stone-100 rounded-2xl rounded-tl-sm px-4 py-2">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Compose */}
      {chatStatus !== CHAT_STATUS.ENDED ? (
        <div className="p-3 border-t border-stone-200 flex-shrink-0">
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Type a message..."
              rows={1}
              className="flex-1 p-2.5 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent resize-none text-stone-700 placeholder-stone-500 text-sm"
              style={{ '--tw-ring-color': primaryColor, maxHeight: '120px' }}
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim() || sending}
              className="p-2.5 text-white rounded-xl transition-colors disabled:opacity-50 hover:opacity-90 flex-shrink-0"
              style={{ backgroundColor: primaryColor }}
              aria-label="Send message"
            >
              <SendIcon />
            </button>
          </div>
        </div>
      ) : (
        <div className="p-4 border-t border-stone-200 text-center flex-shrink-0">
          <p className="text-sm text-stone-500 mb-2">{msgs.chatEnded || 'This chat has ended.'}</p>
          <button
            onClick={startNewChat}
            className="px-4 py-2 text-white rounded-lg font-medium text-sm transition-colors hover:opacity-90"
            style={{ backgroundColor: primaryColor }}
          >
            Start New Chat
          </button>
        </div>
      )}
    </div>
  );
}

// ── Message Bubble Component ──
function MessageBubble({ msg, isOwn, primaryColor, showReadReceipt }) {
  const time = new Date(msg.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const isRead = msg.readBy && Object.keys(msg.readBy).length > 1;

  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} items-end gap-2`}>
      {!isOwn && (
        <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: primaryColor }}>
          {(msg.senderName || 'S')[0].toUpperCase()}
        </div>
      )}
      <div className={`max-w-[75%] ${isOwn ? 'order-first' : ''}`}>
        {!isOwn && msg.senderRole !== 'user' && (
          <p className="text-xs text-stone-500 mb-0.5 ml-1">{msg.senderName || 'Support'}</p>
        )}
        <div
          className={`px-3.5 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${
            isOwn
              ? 'rounded-br-sm text-white'
              : 'rounded-bl-sm bg-stone-100 text-stone-800'
          }`}
          style={isOwn ? { backgroundColor: primaryColor } : undefined}
        >
          {msg.text}
        </div>
        <div className={`flex items-center gap-1 mt-0.5 ${isOwn ? 'justify-end' : 'justify-start'} px-1`}>
          <span className="text-[10px] text-stone-500">{time}</span>
          {isOwn && showReadReceipt && (
            <span className={`${isRead ? 'text-blue-600' : 'text-stone-300'}`}>
              {isRead ? '✓✓' : '✓'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default ChatPanel;
